/**
 * Application chrome: navigation, banners, lock control.
 *
 * Two navigations for one route table. A driver uses this one-handed in a car,
 * where a row of eight tabs across the top of a phone is unusable, so the
 * phone gets a thumb-height tab bar with the four daily destinations and a
 * sheet for the rest. The desktop gets a sidebar, where all eight fit without
 * competing with the page heading.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '@/session/SessionContext';
import { assessBackup } from '@/features/backup/backupStatus';
import { sweepLocks } from '@/features/trips/tripService';
import { Callout } from '@/components/ui';
import { Icon, type IconName } from '@/components/Icon';
import { useT, type Dictionary } from '@/i18n';

interface NavItem {
  to: string;
  label: (t: Dictionary) => string;
  icon: IconName;
  end?: boolean;
  /** Reachable from the phone tab bar; the rest live behind `Mehr`. */
  daily?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: (t) => t.nav.overview, icon: 'grid', end: true, daily: true },
  { to: '/fahrten', label: (t) => t.nav.trips, icon: 'wheel', daily: true },
  { to: '/auftraege', label: (t) => t.nav.orders, icon: 'clipboard', daily: true },
  { to: '/schichten', label: (t) => t.nav.shifts, icon: 'clock', daily: true },
  { to: '/rechnungen', label: (t) => t.nav.invoices, icon: 'invoice' },
  { to: '/export', label: (t) => t.nav.export, icon: 'export' },
  { to: '/sicherung', label: (t) => t.nav.backup, icon: 'shield' },
  { to: '/einstellungen', label: (t) => t.nav.settings, icon: 'settings' },
];

const DAILY_ITEMS = NAV_ITEMS.filter((item) => item.daily);
const MORE_ITEMS = NAV_ITEMS.filter((item) => !item.daily);

export function AppLayout() {
  const session = useSession();
  const location = useLocation();
  const t = useT();
  const [lockedCount, setLockedCount] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const backup = assessBackup(session.app);

  // Freeze anything past its seven-day window as soon as the app opens, so the
  // lock is a property of the data rather than of the app happening to be
  // running on the seventh day.
  useEffect(() => {
    void (async () => {
      const count = await sweepLocks(session.audit, session.app.recordLockDays);
      if (count > 0) setLockedCount(count);
    })();
  }, [session.audit, session.app.recordLockDays]);

  useEffect(() => setMoreOpen(false), [location.pathname]);

  const needsCompanySetup = !session.company.companyName.trim();
  const needsVehicleSetup = session.vehicles.length === 0;
  const onSettings = location.pathname === '/einstellungen';

  return (
    <div className="min-h-dvh lg:flex">
      <aside className="border-line bg-surface hidden shrink-0 border-r lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-64 lg:flex-col">
        <Identity />
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
          {NAV_ITEMS.map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
        </nav>
        <div className="border-line border-t p-3">
          <button
            type="button"
            className="btn-secondary w-full text-xs"
            onClick={session.lock}
          >
            <Icon name="lock" className="h-4 w-4" />
            {t.nav.lock}
          </button>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <header className="border-line bg-canvas/85 sticky top-0 z-20 border-b backdrop-blur lg:hidden">
          <div className="flex items-center gap-3 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="text-heading truncate text-sm font-semibold">
                {session.company.companyName || 'Fahrtenbuch Pro'}
              </p>
              <p className="text-muted truncate text-xs">
                {session.account.displayName} · {session.account.driverCode}
              </p>
            </div>
            <button type="button" className="btn-ghost text-xs" onClick={session.lock}>
              <Icon name="lock" className="h-4 w-4" />
              {t.nav.lock}
            </button>
          </div>
        </header>

        <main className="mx-auto max-w-5xl px-4 py-6 pb-28 lg:px-8 lg:pb-10">
          <div className="mb-5 space-y-3 empty:mb-0">
            {backup.warn && location.pathname !== '/sicherung' && (
              <Callout
                tone={backup.severity === 'overdue' ? 'danger' : 'warning'}
                title={t.shell.backupDueTitle}
              >
                {backup.message(t)}{' '}
                <NavLink to="/sicherung" className="font-medium underline">
                  {t.shell.backupNow}
                </NavLink>
              </Callout>
            )}

            {needsCompanySetup && !onSettings && (
              <Callout tone="info" title={t.shell.companyMissingTitle}>
                {t.shell.companyMissingBody}{' '}
                <NavLink to="/einstellungen" className="font-medium underline">
                  {t.shell.companyMissingLink}
                </NavLink>
              </Callout>
            )}

            {needsVehicleSetup && !onSettings && (
              <Callout tone="info" title={t.shell.vehicleMissingTitle}>
                {t.shell.vehicleMissingBody}{' '}
                <NavLink to="/einstellungen" className="font-medium underline">
                  {t.shell.vehicleMissingLink}
                </NavLink>
              </Callout>
            )}

            {lockedCount > 0 && (
              <Callout tone="info">
                {t.shell.autoLocked(lockedCount)} {t.shell.autoLockedNote}
              </Callout>
            )}
          </div>

          <Outlet />
        </main>

        <footer className="text-muted mx-auto max-w-5xl px-4 pb-28 text-xs leading-relaxed lg:px-8 lg:pb-10">
          <p>{t.shell.footerPrivacy}</p>
          <p className="mt-1">{t.shell.footerDisclaimer}</p>
        </footer>
      </div>

      {moreOpen && (
        <div className="fixed inset-0 z-30 lg:hidden">
          <button
            type="button"
            aria-label={t.nav.closeMenu}
            className="absolute inset-0 bg-black/25"
            onClick={() => setMoreOpen(false)}
          />
          <div className="bg-surface border-line absolute inset-x-0 bottom-0 rounded-t-2xl border-t p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-2xl">
            <div className="bg-line-strong mx-auto mb-3 h-1 w-10 rounded-full" />
            {MORE_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium ${
                    isActive ? 'bg-subtle text-heading' : 'text-body'
                  }`
                }
              >
                <Icon name={item.icon} />
                {item.label(t)}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      <nav className="border-line bg-surface/95 fixed inset-x-0 bottom-0 z-20 grid grid-cols-5 border-t pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden">
        {DAILY_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2 text-[11px] font-medium transition-colors ${
                isActive ? 'text-accent' : 'text-muted'
              }`
            }
          >
            <Icon name={item.icon} />
            {item.label(t)}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen((open) => !open)}
          aria-expanded={moreOpen}
          className={`flex flex-col items-center gap-1 py-2 text-[11px] font-medium transition-colors ${
            moreOpen || MORE_ITEMS.some((item) => item.to === location.pathname)
              ? 'text-accent'
              : 'text-muted'
          }`}
        >
          <Icon name="more" />
          {t.nav.more}
        </button>
      </nav>
    </div>
  );
}

function Identity() {
  const session = useSession();
  return (
    <div className="border-line flex items-center gap-3 border-b px-4 py-4">
      <div className="bg-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white">
        FB
      </div>
      <div className="min-w-0">
        <p className="text-heading truncate text-sm font-semibold">
          {session.company.companyName || 'Fahrtenbuch Pro'}
        </p>
        <p className="text-muted truncate text-xs">
          {session.account.displayName} · {session.account.driverCode}
        </p>
      </div>
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  const t = useT();
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-accent-soft text-accent'
            : 'text-body hover:bg-subtle hover:text-heading'
        }`
      }
    >
      <Icon name={item.icon} />
      {item.label(t)}
    </NavLink>
  );
}
